import { useRef, useState, useEffect, lazy, Suspense } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const Hero3D = lazy(() => import("./Hero3D").then((m) => ({ default: m.Hero3D })));

const ease = [0.22, 1, 0.36, 1];
const H1 = ["Produkte, die", "Aufmerksamkeit", "verdienen."];

export const Hero = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [show3D, setShow3D] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yImg2 = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setTimeout(() => setShow3D(window.innerWidth > 640), 400);
    return () => clearTimeout(t);
  }, []);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 2 - 1;
    const y = ((e.clientY - r.top) / r.height) * 2 - 1;
    mouse.current = { x, y };
    setTilt({ x: y * -6, y: x * 8 });
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      className="relative min-h-[100svh] pt-[76px] overflow-hidden spotlight"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(var(--gold) 1px, transparent 1px), linear-gradient(90deg, var(--gold) 1px, transparent 1px)", backgroundSize: "120px 120px" }} />
      </div>

      <div className="absolute right-[-12%] lg:right-[-4%] top-[-2%] w-[70vw] max-w-[800px] aspect-square hidden sm:block opacity-90" data-testid="hero-3d-canvas">
        {show3D && (
          <Suspense fallback={null}>
            <Hero3D mouse={mouse} />
          </Suspense>
        )}
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100svh-76px)] py-16">
        <motion.div style={{ y: yText, opacity: fade }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5, ease }} className="eyebrow mb-8" data-testid="hero-eyebrow">
            Professionelle KI-Visualisierungen · Amazon · eBay · Onlineshops · Social Media
          </motion.p>

          <h1 className="font-display font-light text-[13vw] sm:text-[4.6rem] lg:text-[6.2rem] xl:text-[7rem] leading-[0.95] tracking-[-0.02em]" data-testid="hero-headline">
            {H1.map((line, i) => (
              <span className="line-mask" key={i}>
                <motion.span
                  className={`block ${i === 1 ? "gold-text italic pr-4" : ""}`}
                  initial={{ y: "115%", rotate: 3 }}
                  animate={{ y: 0, rotate: 0 }}
                  transition={{ duration: 1.2, delay: 0.55 + i * 0.14, ease }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.1, ease }} className="mt-10 max-w-xl text-base md:text-lg text-[var(--muted)] leading-relaxed" data-testid="hero-subline">
            Mehr Aufmerksamkeit. Mehr Vertrauen. Mehr Verkäufe. Salgin Solutions erstellt individuelle, KI-gestützte Produktbilder und technische Grafiken – abgestimmt auf Marke, Zielgruppe und Einsatzzweck.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.3, ease }} className="mt-12 flex flex-wrap gap-4">
            <button onClick={() => scrollToId("preise")} className="btn-gold" data-testid="hero-cta-pricing">
              Preise & Pakete <ArrowUpRight size={16} />
            </button>
            <button onClick={() => scrollToId("portfolio")} className="btn-ghost" data-testid="hero-cta-portfolio">
              Arbeiten ansehen
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.7 }} className="mt-16 grid grid-cols-3 gap-6 max-w-lg border-t border-[var(--line)] pt-6" data-testid="hero-stats">
            {[["ab 7,50 €", "pro KI-Bild"], ["8", "Leistungsbereiche"], ["1", "Ansprechpartner"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-display text-2xl sm:text-3xl text-[var(--text)]">{v}</p>
                <p className="text-[0.55rem] sm:text-[0.65rem] font-mono uppercase tracking-[0.1em] sm:tracking-[0.2em] text-[var(--muted-2)] mt-1 leading-tight">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative h-[460px] sm:h-[560px] lg:h-[640px] mt-4 lg:mt-0" style={{ perspective: 1400 }}>
          <motion.div
            style={{ y: yImg, rotateX: tilt.x, rotateY: tilt.y, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 60, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.9, ease }}
            className="absolute right-0 top-0 lg:top-4 w-[82%] sm:w-[78%] aspect-[4/5] frame frame-corner border border-[var(--line)] shadow-[0_60px_120px_-40px_rgba(0,0,0,0.9)]"
            data-testid="hero-image-main"
          >
            <img src="/images/sauna-pool.webp" alt="KI-Ambientebild einer Fasssauna am Pool" className="absolute inset-0 w-full h-full object-cover object-[45%_50%]" loading="eager" />
            <div className="veil" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between" style={{ transform: "translateZ(30px)" }}>
              <div>
                <p className="eyebrow !text-[0.6rem]">Beispiel</p>
                <p className="font-display text-xl text-[var(--text)] mt-1">KI-Ambientebild</p>
              </div>
              <span className="font-mono text-[0.6rem] text-[var(--muted)]">01 / 06</span>
            </div>
          </motion.div>

          <motion.div
            style={{ y: yImg2, rotateX: tilt.x * 1.4, rotateY: tilt.y * 1.4 }}
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 1.4, ease }}
            className="absolute left-0 bottom-0 w-[46%] sm:w-[52%] aspect-[5/4] frame border border-[var(--line-strong)] bg-white shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]"
            data-testid="hero-image-sketch"
          >
            <img src="/images/skizze.jpg" alt="Technische Produktgrafik" className="absolute inset-0 w-full h-full object-contain p-2 bg-white" />
            <div className="absolute top-3 left-3 bg-[var(--bg)] text-[var(--gold)] font-mono text-[0.55rem] tracking-[0.2em] uppercase px-2 py-1">Skizze</div>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        onClick={() => scrollToId("problem")}
        className="absolute bottom-8 left-6 lg:left-10 hidden lg:flex items-center gap-3 text-[var(--muted-2)] hover:text-[var(--gold)] transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
        <span className="font-mono text-[0.6rem] tracking-[0.3em] uppercase">Scrollen</span>
      </motion.button>
    </section>
  );
};
